import type { Begehung } from "../ortsbegehung";
import type { Aufnahme } from "../routen";
import type { Aufgabe } from "../vorbereitung";
import type { Nachfasszeile } from "../verkaufsmappe";
import type { Angebot, Aufnahmetermin } from "../typen";

/* ══════════════════════════════════════════════════════════════════
   Die Ortsaufnahmen des Prototyps — 18.09.2026.

   **Warum eine eigene Datei neben `daten.ts`.** Die Ortsaufnahme wird von
   drei Stellen gelesen (`ortsbegehung.ts` am Telefon, `routen.ts` am
   Schreibtisch, `verkaufsmappe.ts` für den Auszug) **und** von einer
   verändert: Wer „Ich mache das" drückt, zieht sie an sich, und sie muss
   danach im Pool weg sein. Ein Stand, der an zwei Stellen liegt, läuft
   auseinander — dieselbe Begründung, aus der `routen.ts` seine Belegungen
   in **einer** Liste hält (*„Die Attrappe hält ihren Stand für die Dauer
   der Sitzung"*).

   `daten.ts` bleibt, was es ist: reine Beispieldaten ohne Verhalten. Hier
   stehen die Zeilen **und** der Stand, der sich über eine Sitzung ändert.

   ── Warum es die Datei überhaupt gibt ─────────────────────────────

   Bis heute gab `daten/ortsbegehung.ts` unter `VITE_ATTRAPPE=1` eine leere
   Liste zurück und warf beim Öffnen einer einzelnen Begehung. Das war
   nicht nur eine Messhürde: **Lisa geht nach
   [`docs/29-pruefauftrag.md`](../../../../../docs/29-pruefauftrag.md) durch
   den Prototyp** — und sah dort einen Schirm, auf dem nie etwas stand.

   ── Der Fall ist der aus der echten Datenbank ─────────────────────

   Nachgebaut aus `supabase/beispiele/ortsaufnahme.mjs`, bis in die Sätze:
   *Solarpark Emsland Betriebs GmbH*, zwei PV-Freiflächen — **Feld Süd**
   erledigt mit 1.240 m² bei starker Verschmutzung, **Feld Nord** offen
   und ohne Grad. Dazu zwei weitere im Pool, damit beide Abschnitte und
   beide Punktformen zu sehen sind.

   > **⚠ Alle Zahlen sind Beispielwerte.** 1.240 m² ist eine erfundene
   > Fläche, und **ein Preis steht hier nirgends**: Die Verschmutzungsgrade
   > tragen nur die Auskunft, *dass* ein Satz gepflegt ist (`saetze: 1`),
   > und der Zuschlag trägt `platzhalter: true`. Was ein Quadratmeter kostet,
   > sagt die Kalkulation
   > aus `04-preisfindung.md` und nicht der Prototyp (CLAUDE.md, „Was ohne
   > Rückfrage nicht gesetzt werden darf").
   ══════════════════════════════════════════════════════════════════ */

/** Derselbe Mensch wie in `daten.ts` — `ICH.id`, ohne Ringschluss im Import. */
const MEINE_ID = "u-001";
const MEIN_NAME = "Lisa Meyer";

/* Der Tag von „heute" in der Uhr des Geräts, um `tage` Tage zurück. Die
   Attrappe rechnet relativ: Ein festes Datum wäre nach einer Woche „seit
   9 Tagen offen", und das sieht aus wie eine liegengebliebene Aufgabe. */
function vorTagen(tage: number): string {
  const d = new Date();
  d.setDate(d.getDate() - tage);
  d.setHours(9, 20, 0, 0);
  return d.toISOString();
}

/** Morgen, von `ab` bis `bis` Uhr in der Uhr des Geräts — ein Termin, relativ wie alles hier. */
function terminMorgen(ab: number, bis: number): Aufnahmetermin {
  const von = new Date();
  von.setDate(von.getDate() + 1);
  von.setHours(ab, 0, 0, 0);
  const ende = new Date(von);
  ende.setHours(bis, 0, 0, 0);
  return { von: von.toISOString(), bis: ende.toISOString(), person: MEIN_NAME, personId: MEINE_ID };
}

/** `12.09.` — dieselbe kurze Form, die `alsAufnahme()` aus der API baut. */
function tagKurz(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const zz = (n: number) => String(n).padStart(2, "0");
  return `${zz(d.getDate())}.${zz(d.getMonth() + 1)}.`;
}

/* ══════════════ Was die Leistungsart mitbringt ══════════════

   Drei Grade und ein Zuschlag — dieselbe Aufstellung, die
   `GET /aufnahmen/:id` aus `service_type_variant` und `service_surcharge`
   zusammenstellt (Migrationen 0142 und 0152). Zwei Preistreiber standen hier
   bis zum 21.09.2026 daneben; die Gattung ist abgebaut (Migration 0164).

   **`saetze: 1` ist eine Anzahl, kein Betrag.** Es heißt „für diesen Grad
   ist ein Satz je Einheit gepflegt" — ohne zu sagen, welcher. Stünde dort
   `0`, sagte die Maske „ohne Satz kommt kein Preis heraus", und das wäre
   für den Durchgang die falsche Auskunft. */
const GRADE = [
  { id: "k-leicht", code: "LEICHT", bezeichnung: "Leicht verschmutzt", saetze: 1 },
  { id: "k-mittel", code: "MITTEL", bezeichnung: "Mittel verschmutzt", saetze: 1 },
  { id: "k-stark", code: "STARK", bezeichnung: "Stark verschmutzt", saetze: 1 },
];

/* `platzhalter: true` heißt am Zuschlag genau das, was es sagt: Der Betrag
   ist erfunden und mit niemandem abgestimmt (Migration 0036). Hier steht er
   deshalb gar nicht erst — `saetze: []`, und die Maske sagt „kein Satz für
   diesen Grad". */
const ZUSCHLAEGE = [
  { id: "z-flechten", code: "FLECHTEN", bezeichnung: "Flechten und Moos", platzhalter: true, saetze: [] },
];

/** Was jede Zeile von der Leistungsart erbt — an einer Stelle. */
const LEISTUNG = {
  metric: "QM",
  einheit: "m²",
  kategorien: GRADE,
  zuschlaege: ZUSCHLAEGE,
  /* **Die Leistungsart selbst** (Migration 0154). Aus ihr holt der Schirm
     die Lage — welches Gerät und welcher Mensch in der gewählten Woche frei
     ist. Im Attrappenbetrieb gibt es dafür keine Antwort; der Abschnitt
     bleibt dort leer, statt eine erfundene Zusage zu zeigen. */
  leistungsartId: "lt-pv-reinigung",
  /* **Die Woche aus dem Angebot** — der Rahmen, in dem vor Ort der Tag
     gewählt wird. `zeitschaerfe: "kw"` heißt: Montag und Sonntag, und
     niemand hat einen Termin zugesagt. */
  zeitschaerfe: "kw" as const,
};

/** Montag der Woche in `n` Wochen — der Rahmen einer grob geplanten Zeile. */
function montagIn(wochen: number): string {
  const d = new Date();
  d.setDate(d.getDate() - ((d.getDay() + 6) % 7) + wochen * 7);
  return d.toISOString().slice(0, 10);
}

const sonntagZu = (montag: string): string => {
  const d = new Date(`${montag}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() + 6);
  return d.toISOString().slice(0, 10);
};

/** Die Woche einer Zeile, fertig in der Form der API. */
function woche(wochen: number): { ausfuehrungVon: string; ausfuehrungBis: string } {
  const von = montagIn(wochen);
  return { ausfuehrungVon: von, ausfuehrungBis: sonntagZu(von) };
}

/* **Der Preis steht im Prototyp ohne Betrag.** Was die PV-Reinigung je
   Quadratmeter kostet, hat niemand beschlossen (CLAUDE.md, 04-preisfindung.md)
   — eine erfundene Summe wäre genau die Zahl, die jemand im Durchgang für
   abgestimmt hielte. `null` heißt hier: Es hängt kein Angebot daran, das
   sich beziffern ließe, und der Abschnitt fehlt. */
const OHNE_PREIS = { preis: null };

/* ══════════════ Die fünf Zeilen ══════════════ */

export const BEGEHUNGEN: Begehung[] = [
  /* 1 · Die erledigte — das ausgefüllte Beispiel. Sie steht unter dem
     Reiter „Erledigt" und lässt sich öffnen; dort ist zu sehen, was eine
     Begehung mitbringt, wenn sie durch ist. */
  {
    id: "sa-1",
    status: "erledigt",
    kunde: "Solarpark Emsland Betriebs GmbH",
    kundennummer: "10041",
    ort: "Solarpark Emsland — Feld Süd",
    ortschaft: "Haren",
    ortId: "so-sued",
    vorgang: "AG-2026-0051",
    betreff: "Feld Süd, Frühjahrsreinigung",
    position: "PV-Reinigung — Feld Süd, Frühjahrsreinigung",
    ...LEISTUNG,
    ...woche(1),
    ...OHNE_PREIS,
    wer: MEIN_NAME,
    werId: MEINE_ID,
    bestelltIso: vorTagen(6),
    termin: null,
    erledigtIso: vorTagen(4),
    // Beispielwert: 1.240 Module × 1,00 m², nicht gemessen und kein Preis.
    menge: 1240,
    notiz: "Fläche aus dem Anlagendatenblatt (1.240 Module × 1,00 m²) und vor Ort "
      + "stichprobenhaft nachgemessen. Starke Verschmutzung: angrenzender Maisacker, "
      + "Rückstände auf der unteren Modulreihe. Wasser ist auf dem Gelände vorhanden, "
      + "Strom am Trafohaus.",
    ansprechpartner: "Herr Tebben, Betriebsführung (vor Ort angetroffen)",
    zufahrt: "Über den Feldweg ab der K 271, letzte 400 m geschottert. Tor mit Zahlenschloss.",
    untergrund: "Grasnarbe zwischen den Reihen, nach Regen weich — Radlader nur mit Breitreifen.",
    hindernisse: "Reihenabstand 2,80 m. Trafostation in der Mitte, Zaun ohne zweites Tor.",
    /* **Wo das Gerät steht** (0156) — die Frage, die bei jedem Gerät mit
       Abstützung zuerst kommt. Sie steht hier ausgefüllt, damit im Prototyp
       zu sehen ist, wofür das Feld da ist. */
    stellplatz: "Wendeplatte am Trafohaus, geschottert, rund 15 x 10 m. Kran nur mit "
      + "kurzer Abstützung nach Norden — nach Süden stehen die Reihen zu dicht.",
    kategorieGewaehlt: "k-stark",
    zuschlaegeGewaehlt: [],
  },
  /* 2 · Die offene bei mir — derselbe Kunde, das zweite Feld. Sie steht
     unter „Meine Begehungen" mit gefülltem grünem Punkt und ist die, an
     der sich der ganze Weg vor Ort durchspielen lässt. */
  {
    id: "sa-2",
    status: "offen",
    kunde: "Solarpark Emsland Betriebs GmbH",
    kundennummer: "10041",
    ort: "Solarpark Emsland — Feld Nord",
    ortschaft: "Haren",
    ortId: "so-nord",
    vorgang: "AG-2026-0052",
    betreff: "Feld Nord, Erstreinigung",
    position: "PV-Reinigung — Feld Nord, Erstreinigung",
    ...LEISTUNG,
    ...woche(2),
    ...OHNE_PREIS,
    wer: MEIN_NAME,
    werId: MEINE_ID,
    bestelltIso: vorTagen(2),
    /* **Mit Termin** (Migration 0180): morgen 09:00–10:00, bei mir. Die eine
       Aufnahme im Prototyp, an der sich zeigt, wie ein Termin aussieht —
       in der Liste, in der Begehung und auf „Heute". */
    termin: terminMorgen(9, 10),
    erledigtIso: "",
    menge: null,
    notiz: "",
    ansprechpartner: "",
    /* **Was am Ort steht, steht schon da.** Beide Felder gehören derselben
       Anlage: Wer beim zweiten Mal hinfährt, korrigiert die Zufahrt, statt
       sie neu zu tippen (Regel 11, 19-ortsaufnahme.md `[4]`). */
    zufahrt: "Über den Feldweg ab der K 271, letzte 400 m geschottert. Tor mit Zahlenschloss.",
    untergrund: "",
    hindernisse: "",
    stellplatz: "",
    kategorieGewaehlt: "",
    zuschlaegeGewaehlt: [],
  },
  /* 3 · Eine im Pool, mit Ort — offener Ring, „Ich mache das". */
  {
    id: "sa-3",
    status: "offen",
    kunde: "Meyer Bau",
    kundennummer: "10044",
    ort: "Halle 3",
    ortschaft: "Lohne",
    ortId: "so-halle3",
    vorgang: "AG-2026-0053",
    betreff: "Dachreinigung Halle 3",
    position: "PV-Reinigung — Dachreinigung Halle 3",
    ...LEISTUNG,
    ...woche(3),
    ...OHNE_PREIS,
    wer: "",
    werId: "",
    bestelltIso: vorTagen(1),
    termin: null,
    erledigtIso: "",
    menge: null,
    notiz: "",
    ansprechpartner: "",
    zufahrt: "",
    untergrund: "",
    hindernisse: "",
    stellplatz: "",
    kategorieGewaehlt: "",
    zuschlaegeGewaehlt: [],
  },
  /* 4 · Eine im Pool **ohne Einsatzort** — der Neukunde, bei dem es die
     Baustelle noch nicht gibt (`site_id` darf fehlen, Migration 0091). Die
     Maske sagt dann, dass Zufahrt, Untergrund und Hindernisse nirgends
     abgelegt werden; nur die Position bekommt die Zahl. Ohne diese Zeile
     wäre der Fall im Prototyp nicht zu sehen. */
  {
    id: "sa-4",
    status: "offen",
    kunde: "Hempen Agrar GmbH",
    kundennummer: "10061",
    ort: "",
    ortschaft: "",
    ortId: "",
    vorgang: "AG-2026-0054",
    betreff: "Dachflächen Hofstelle",
    position: "PV-Reinigung — Dachflächen Hofstelle",
    ...LEISTUNG,
    ...woche(4),
    ...OHNE_PREIS,
    wer: "",
    werId: "",
    bestelltIso: vorTagen(0),
    termin: null,
    erledigtIso: "",
    menge: null,
    notiz: "",
    ansprechpartner: "",
    zufahrt: "",
    untergrund: "",
    hindernisse: "",
    stellplatz: "",
    kategorieGewaehlt: "",
    zuschlaegeGewaehlt: [],
  },
  /* 5 · **Gemessen, das Angebot fehlt** (Befund 41, 25.09.2026) — selbst
     vor Ort angelegt, ohne Vorgang, schon aufgemessen. Sie steht weiter
     unter „offen": Lisa, *„Die Liste soll dann weiter zeigen, dass hier noch
     etwas zu tun ist."* Hier lassen sich „Angebot daraus machen" und „Ohne
     Angebot abschließen" ansehen. */
  {
    id: "sa-5",
    status: "offen",
    kunde: "Kruse GaLaBau",
    kundennummer: "10052",
    ort: "Hallendach Kruse",
    ortschaft: "Meppen",
    ortId: "so-kruse",
    vorgang: "",
    betreff: "",
    position: "",
    ...LEISTUNG,
    ...woche(2),
    ...OHNE_PREIS,
    wer: MEIN_NAME,
    werId: MEINE_ID,
    bestelltIso: vorTagen(1),
    termin: null,
    erledigtIso: vorTagen(0),
    // Beispielwert, nicht gemessen und kein Preis.
    menge: 860,
    notiz: "",
    ansprechpartner: "",
    zufahrt: "",
    untergrund: "",
    hindernisse: "",
    stellplatz: "",
    kategorieGewaehlt: "k-mittel",
    zuschlaegeGewaehlt: [],
  },
];

/** Die drei Bilder der erledigten Begehung — Pfade, wie sie die Ablage trägt. */
export const FOTOPFADE: Record<string, string[]> = {
  "sa-1": [
    "attrappe/aufnahme/sa-1/modulfeld-1.jpg",
    "attrappe/aufnahme/sa-1/modulfeld-2.jpg",
    "attrappe/aufnahme/sa-1/zufahrt.jpg",
  ],
};

/* ══════════════ Nachfassen ══════════════

   Zwei Angebote mit laufender Frist — eines abgelaufen, eines eilig. Beide
   liegen als Angebot schon in `daten.ts`; hier steht nur, was der Auszug
   am Telefon davon braucht. Beträge stehen darin nicht. */
export const NACHFASSEN: Nachfasszeile[] = [
  {
    vorgang: "AG-2026-0041",
    betreff: "Radlader 5 t · 3 Wochen",
    kunde: "Meyer Bau",
    kundennummer: "10044",
    telefon: "+49 4442 123456",
    ort: "",
    ortschaft: "Lohne",
    frist: "",
    tage: -2,
    eilt: false,
  },
  {
    vorgang: "AG-2026-0044",
    betreff: "PV-Reinigung · Feld B",
    kunde: "Solar Nord GmbH",
    kundennummer: "10019",
    telefon: "+49 441 998877",
    ort: "Solarpark Nord, Feld B",
    ortschaft: "Garrel",
    frist: "",
    tage: 1,
    eilt: true,
  },
];

/* ══════════════════════════════════════════════════════════════════
   Der Stand für die Dauer der Sitzung

   Derselbe Griff wie bei den Belegungen in `routen.ts`: eine veränderliche
   Kopie, die lebt, solange der Reiter offen ist. **Ohne sie täte „Ich mache
   das" nichts** — die Maske frischt danach ihre Liste auf und bekäme
   dieselbe Zeile im Pool zurück. Genau das soll Lisa aber ausprobieren
   können.
   ══════════════════════════════════════════════════════════════════ */

let stand: Begehung[] = BEGEHUNGEN.map((b) => ({ ...b }));

/** Alle Zeilen, in der Reihenfolge, in der sie bestellt wurden. */
export const alleBegehungen = (): Begehung[] => stand.map((b) => ({ ...b }));

/** Die offenen — dieselbe Auswahl, die `GET /aufnahmen?status=offen` trifft. */
export const offeneBegehungen = (): Begehung[] =>
  stand.filter((b) => b.status === "offen").map((b) => ({ ...b }));

/** Eine einzelne. `null` heißt: Die Kennung gibt es nicht. */
export const eineBegehung = (id: string): Begehung | null => {
  const b = stand.find((x) => x.id === id);
  return b ? { ...b } : null;
};

/** Gemessen: erledigt, oder offen mit eingetragenem Aufmaß (Befund 41). */
function begehungGemessen(b: Begehung): boolean {
  return b.status === "erledigt" || (b.status === "offen" && b.erledigtIso !== "");
}

/**
 * **Gemessen, und es wird kein Angebot daraus** — wie
 * `POST /aufnahmen/:id/abschliessen` (Befund 41).
 */
export function begehungAbschliessen(id: string): void {
  aendern(id, (b) => (b.status === "offen" && begehungGemessen(b) && !b.vorgang
    ? { ...b, status: "erledigt" }
    : b));
}

/**
 * **Was am Entwurf hängt** — dieselben Felder wie die Angebotsliste der API
 * (Befund 40): eine durchgeführte Aufnahme sperrt das Verwerfen, eine nur
 * bestellte geht mit.
 */
export function angebotsbezug(nr: string): Partial<Pick<Angebot,
  "hatAufnahme" | "aufnahmeGemessenAm" | "fotosDraussen" | "aufnahmeBestellt" | "aufnahmeTermin">> {
  const daran = stand.filter((b) => b.vorgang === nr);
  const gemessen = daran.find((b) => b.status !== "verworfen" && begehungGemessen(b));
  const fotos = daran.reduce((n, b) => n + (FOTOPFADE[b.id]?.length ?? 0), 0);
  const bestellt = daran.find((b) => b.status === "offen" && !begehungGemessen(b));
  return {
    ...(gemessen || fotos ? {
      hatAufnahme: true,
      ...(gemessen?.erledigtIso ? { aufnahmeGemessenAm: new Date(gemessen.erledigtIso).toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric" }) } : {}),
      fotosDraussen: fotos,
    } : {}),
    ...(bestellt ? { aufnahmeBestellt: true } : {}),
    ...(bestellt?.termin ? { aufnahmeTermin: { von: bestellt.termin.von, person: bestellt.termin.person } } : {}),
  };
}

/** Der Entwurf ist verworfen: Seine nur bestellten Fahrten gehen mit (Befund 40). */
export function begehungenDesEntwurfsVerwerfen(nr: string): void {
  stand = stand.map((b) => (b.vorgang === nr && b.status === "offen" && !begehungGemessen(b)
    ? { ...b, status: "verworfen", termin: null, vorgang: "", position: "" }
    : b));
}

function aendern(id: string, wie: (b: Begehung) => Begehung): void {
  stand = stand.map((b) => (b.id === id ? wie(b) : b));
}

/** An sich ziehen — oder mit `wer: null` zurücklegen. */
export function begehungNehmen(id: string, wer?: null): void {
  aendern(id, (b) => (wer === null
    ? { ...b, wer: "", werId: "" }
    : { ...b, wer: MEIN_NAME, werId: MEINE_ID }));
}

/** Die Fahrt entfällt. Gelöscht wird nichts — sie bekommt einen Grund. Ihr Termin geht mit (0180). */
export function begehungVerwerfen(id: string, grund: string): void {
  aendern(id, (b) => ({ ...b, status: "verworfen", notiz: grund, termin: null, erledigtIso: new Date().toISOString() }));
}

/**
 * Einen Termin setzen, verlegen oder mit `null` streichen — wie
 * `PUT`/`DELETE /aufnahmen/:id/termin`. Die Person ist im Prototyp immer
 * „ich"; liegt die Aufnahme im Pool, gehört sie danach mir, wie in der Route.
 */
export function begehungTermin(id: string, termin: { von: string; bis: string } | null): void {
  aendern(id, (b) => (termin
    ? {
      ...b,
      termin: { von: termin.von, bis: termin.bis, person: MEIN_NAME, personId: MEINE_ID },
      ...(b.werId ? {} : { wer: MEIN_NAME, werId: MEINE_ID }),
    }
    : { ...b, termin: null }));
}

/** Was vor Ort aufgenommen wurde — dieselben Felder wie `POST /…/ergebnis`. */
export function begehungErgebnis(id: string, e: {
  menge: number;
  notiz?: string | undefined;
  ansprechpartner?: string | undefined;
  zufahrt?: string | undefined;
  untergrund?: string | undefined;
  hindernisse?: string | undefined;
  stellplatz?: string | undefined;
  ortId?: string | undefined;
  kategorie?: string | null | undefined;
  zuschlaege?: { id: string; menge: number | null }[] | undefined;
}): void {
  /* **Was leer bleibt, löscht nichts.** Dieselbe Regel wie in der Route
     (`coalesce`, belegt in `supabase/pruefungen/handy-aufnahme.mjs`,
     Schritt 7): Die Zufahrt vom letzten Mal steht weiter da. */
  const behalten = (neu: string | undefined, alt: string) => (neu && neu.trim() ? neu.trim() : alt);
  aendern(id, (b) => ({
    ...b,
    /* **Erledigt nur, wenn es das Angebot schon gibt** (Befund 41, wie die
       Route): Die bestellte hängt an einer Position; die selbst angelegte
       bleibt offen und gemessen, bis ein Angebot daraus wird. */
    status: b.position ? "erledigt" : "offen",
    menge: e.menge,
    erledigtIso: new Date().toISOString(),
    notiz: behalten(e.notiz, b.notiz),
    ansprechpartner: behalten(e.ansprechpartner, b.ansprechpartner),
    zufahrt: behalten(e.zufahrt, b.zufahrt),
    untergrund: behalten(e.untergrund, b.untergrund),
    hindernisse: behalten(e.hindernisse, b.hindernisse),
    stellplatz: behalten(e.stellplatz, b.stellplatz),
    /* **Der nachgetragene Einsatzort** (0156): Beim Neukunden gab es die
       Baustelle beim Bestellen noch nicht. Erst mit ihr haben Zufahrt,
       Untergrund und Stellplatz einen Platz zum Stehen. */
    ...(e.ortId ? { ortId: e.ortId } : {}),
    kategorieGewaehlt: e.kategorie === undefined ? b.kategorieGewaehlt : (e.kategorie ?? ""),
    zuschlaegeGewaehlt: e.zuschlaege?.map((z) => z.id) ?? b.zuschlaegeGewaehlt,
    zuschlaegeFlaechen: e.zuschlaege
      ? Object.fromEntries(e.zuschlaege.map((z) => [z.id, z.menge]))
      : b.zuschlaegeFlaechen ?? {},
  }));
}

/* ══════════════ Dieselben Zeilen für den Schreibtisch ══════════════

   `/aufnahmen` am großen Schirm liest eine flachere Form (`Aufnahme` in
   `routen.ts`): Tabelle links, Blatt rechts. Sie wird **aus demselben
   Stand abgeleitet** und nicht zweitgepflegt — sonst zeigte der Schreibtisch
   im Pool, was am Telefon längst genommen ist. */
export const alsAufnahmen = (): Aufnahme[] =>
  stand.map((b): Aufnahme => ({
    id: b.id,
    status: b.status,
    kunde: b.kunde,
    ort: b.ort || null,
    ortschaft: b.ortschaft || null,
    vorgang: b.vorgang || null,
    betreff: b.betreff || null,
    position: b.position || null,
    metric: b.metric,
    einheit: b.einheit,
    wer: b.wer || null,
    werId: b.werId || null,
    leistungsartId: b.leistungsartId || null,
    wann: tagKurz(b.bestelltIso),
    menge: b.menge,
    gemessen: begehungGemessen(b),
    gemessenAm: begehungGemessen(b) && b.erledigtIso ? tagKurz(b.erledigtIso) : "",
    notiz: b.notiz || null,
    termin: b.termin,
  }));

/** Tage bis zum Tag eines Zeitpunkts, im Kalender des Geräts — 0 heute, 1 morgen. */
function tageBis(iso: string): number {
  const ziel = new Date(iso);
  const heute = new Date();
  const tag = (d: Date) => Date.UTC(d.getFullYear(), d.getMonth(), d.getDate());
  return Math.round((tag(ziel) - tag(heute)) / 86_400_000);
}

/* ══════════════ Dieselben Zeilen als Aufgabe ══════════════

   **Seit dem 19.09.2026 stehen die offenen auch als Aufgabe da** — auf
   „Heute" und dem Start am Telefon. Luca: *„Wenn wir eine offene
   Ortsaufnahme haben, dann muss die Aufgabe ja schön auf dem Handy auch
   auftauchen."* Die echte API liefert sie in `GET /handy/heute` und
   `GET /start` als `Aufgabe` (`dienste/aufgaben.ts`, `aufnahmenLesen`);
   hier entsteht dieselbe Form **aus demselben Stand** — wer im Prototyp
   „Ich mache das" drückt, sieht die Zeile auf „Heute" wandern.

   Lisa ist Disposition und Verwaltung und sieht deshalb alle offenen,
   wie in der Route (`lenkt`). */
export const alsAufgaben = (): Aufgabe[] =>
  // Gemessen ist die Fahrt gemacht (Befund 41) — wie `aufnahmenLesen`.
  stand.filter((b) => b.status === "offen" && !begehungGemessen(b)).map((b): Aufgabe => ({
    art: "ortsaufnahme",
    id: `ortsaufnahme-${b.id}`,
    belegungId: null,
    aufnahmeId: b.id,
    ressourceId: null,
    kennung: b.vorgang,
    typ: "",
    kunde: b.kunde,
    ort: b.ort,
    ortschaft: b.ortschaft,
    vorgang: b.vorgang,
    /* Mit Termin folgt die Aufgabe dem Termin, wie in `aufnahmenLesen`. */
    faelligIso: b.termin?.von ?? b.bestelltIso,
    tage: b.termin ? tageBis(b.termin.von) : null,
    ueberfaellig: b.termin ? new Date(b.termin.bis).getTime() <= Date.now() : false,
    termin: b.termin,
    angelegtIso: b.bestelltIso,
    genommen: b.werId ? { id: b.werId, name: b.wer } : null,
    bediener: [],
    pool: "aussendienst",
    meins: b.werId === MEINE_ID,
    darfNehmen: !b.werId,
    darfZurueckgeben: !!b.werId,
    maengel: [],
    messung: { metric: b.metric, einheit: b.einheit || b.metric },
  }));
